/**
 * Le peu de calcul colorimétrique dont la marque a besoin, et rien de plus.
 *
 * Il vit ici et pas dans une feuille de style pour une raison précise : `color-mix`
 * sait mélanger deux couleurs, il ne sait pas dire si le résultat est LISIBLE. Tant
 * que la dérivation de la navigation vivait dans `tokens.css`, la couleur de son
 * texte ne pouvait qu'être écrite en dur — et elle l'était, en blanc.
 *
 * Mesuré le 02/09/2026. Le défaut est LATENT, pas visible : les six écoles semées
 * passent aujourd'hui, la plus juste étant ACE Education à 4,97:1 pour un seuil AA
 * de 4,5:1. Il mord dès qu'une couleur est claire — un jaune tombe à 2,09:1, un
 * lime à 1,89:1 — et rien n'empêche une école d'en avoir une.
 */

export type Rgb = [number, number, number];

/**
 * Le format des tokens vit dans `shared/` : le serveur le refuse à l'écriture, le
 * front l'ignore à l'affichage, et deux expressions pour un format finiraient par
 * ne plus dire la même chose.
 */
import { HSL_TRIPLET } from "@shared/brand.types";

export { HSL_TRIPLET };

export function parseHsl(triplet: string): [number, number, number] | null {
	if (!HSL_TRIPLET.test(triplet)) return null;
	const [h, s, l] = triplet.split(/\s+/).map(Number.parseFloat) as [number, number, number];
	// Une valeur hors bornes ne « dégrade » pas, elle produit une couleur fausse :
	// mieux vaut retomber sur le thème Mentor Goal.
	if (h > 360 || s > 100 || l > 100) return null;
	return [h, s, l];
}

export function hslToRgb(h: number, s: number, l: number): Rgb {
	const saturation = s / 100;
	const lightness = l / 100;
	const k = (n: number) => (n + h / 30) % 12;
	const a = saturation * Math.min(lightness, 1 - lightness);
	const channel = (n: number) =>
		lightness - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
	return [Math.round(channel(0) * 255), Math.round(channel(8) * 255), Math.round(channel(4) * 255)];
}

export function toHex([r, g, b]: Rgb): string {
	return `#${[r, g, b].map((c) => c.toString(16).padStart(2, "0")).join("")}`;
}

/**
 * Le chemin du retour : `#112233` vers « 210 50% 13% ».
 *
 * Il existe parce que le sélecteur de couleur du navigateur ne parle QUE
 * hexadécimal, et que la base ne stocke QUE des triplets HSL. Sans lui, une école
 * choisirait sa couleur dans un champ texte où « 212 65% 24% » se tape à la main.
 *
 * L'arrondi est à UNE décimale, et ce n'est pas de la coquetterie : arrondir à
 * l'entier fait dériver l'aller-retour jusqu'à 2/255 par canal, une décimale le
 * ramène à 0. Une école qui prend son bleu de marque doit retrouver EXACTEMENT ce
 * bleu, pas un voisin.
 *
 * `tests/brand.test.ts` balaye 4 096 couleurs et exige l'égalité stricte : le
 * chiffre est rejoué à chaque `validate`, il ne vit pas dans ce commentaire.
 */
export function hexToHslTriplet(hex: string): string | null {
	const clean = hex.trim().replace(/^#/, "");
	if (!/^[0-9a-fA-F]{6}$/.test(clean)) return null;
	const [r, g, b] = [0, 2, 4].map((at) => Number.parseInt(clean.slice(at, at + 2), 16) / 255) as [
		number,
		number,
		number,
	];

	/** Une décimale, sans le « .0 » quand il ne dit rien. */
	const arrondi = (value: number) => String(Math.round(value * 10) / 10);

	const max = Math.max(r, g, b);
	const min = Math.min(r, g, b);
	const l = (max + min) / 2;
	const d = max - min;
	if (d === 0) return `0 0% ${arrondi(l * 100)}%`;

	const s = d / (1 - Math.abs(2 * l - 1));
	let h: number;
	if (max === r) h = ((g - b) / d) % 6;
	else if (max === g) h = (b - r) / d + 2;
	else h = (r - g) / d + 4;
	h *= 60;
	if (h < 0) h += 360;

	return `${arrondi(h % 360)} ${arrondi(s * 100)}% ${arrondi(l * 100)}%`;
}

/** `ratio` part de `from` : 1 rend `from` intact, 0 rend `towards`. */
export function mix(from: Rgb, towards: Rgb, ratio: number): Rgb {
	return from.map((channel, index) =>
		Math.round(channel * ratio + (towards[index] ?? 0) * (1 - ratio)),
	) as Rgb;
}

/** Luminance relative WCAG. */
export function luminance([r, g, b]: Rgb): number {
	const linear = (value: number) => {
		const c = value / 255;
		return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
	};
	return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);
}

export function contrast(a: Rgb, b: Rgb): number {
	const [lighter, darker] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
	return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Le texte qui se lit sur ce fond : l'encre ou le papier, celui des deux qui tient
 * le meilleur contraste.
 *
 * Deux valeurs et pas un dégradé : ce sont les deux seules couleurs de texte du
 * référentiel, et en choisir une troisième reviendrait à inventer un token.
 */
export function readableOn(background: Rgb, ink: Rgb, paper: Rgb): Rgb {
	return contrast(ink, background) >= contrast(paper, background) ? ink : paper;
}

/**
 * Le ton atténué : aussi effacé que possible SANS passer sous le seuil.
 *
 * Un ratio fixe ne marche pas — le même « 62 % vers le fond » donne 4,1:1 sur un
 * aplat sombre et 2,77:1 sur un aplat clair, parce que la luminance ne varie pas
 * linéairement. On s'éloigne donc du texte pas à pas, et on garde le dernier pas
 * qui tient. La valeur n'est pas choisie, elle est trouvée.
 */
/**
 * Un état d'un aplat — survol, sélection — qui doit tenir DEUX promesses.
 *
 * 1. se VOIR : se distinguer de l'aplat, sinon survoler ne montre rien ;
 * 2. rester LISIBLE : garder le texte au-dessus du seuil.
 *
 * `separation` dit à quel point l'état doit se détacher de l'aplat : le survol est
 * discret, la sélection franche.
 *
 * ⚠️ LE SENS compte, et il vient du référentiel. Le proto éclaircit : son aplat est
 * #263171, son survol #344a7d, sa sélection #455f9b. On allait dans l'autre sens —
 * « s'éloigner du texte », donc vers le noir sur une barre sombre — et l'item
 * sélectionné sortait presque noir là où le proto le rend visiblement plus clair.
 *
 * On va donc VERS le texte en premier, tant que le texte reste au-dessus du seuil.
 * Ce n'est pas gratuit : sur un aplat clair, se rapprocher de l'encre épuise vite la
 * marge, et on bascule alors dans l'autre sens plutôt que de rendre illisible.
 *
 * ⚠️ `separation` a d'abord été un PAS, et c'était creux : les boucles s'arrêtent au
 * premier candidat qui franchit le seuil, donc la taille du pas ne changeait que le
 * nombre d'itérations. Le survol rendait #3f38b0 et la sélection #3e37ae — la même
 * couleur. Les deux états existaient dans le code et pas à l'écran.
 */
export function stateOn(
	background: Rgb,
	foreground: Rgb,
	separation: number,
	minimumText = 4.5,
): Rgb {
	const away = luminance(foreground) > 0.5 ? [0, 0, 0] : [255, 255, 255];
	const separates = (candidate: Rgb) => contrast(candidate, background) >= separation;
	const readable = (candidate: Rgb) => contrast(foreground, candidate) >= minimumText;

	// D'abord VERS le texte, le sens du référentiel : sur une barre sombre à texte
	// blanc, l'état s'éclaircit. On s'arrête dès qu'il se détache assez, et jamais
	// au-delà de ce que la lisibilité permet.
	for (let step = 1; step <= 40; step++) {
		const candidate = mix(background, foreground, 1 - step / 40);
		if (!readable(candidate)) break;
		if (separates(candidate)) return candidate;
	}

	// Cette direction est épuisée — un aplat clair sous une encre sombre n'a que
	// quelques crans avant de devenir illisible. On repart de l'autre côté, où la
	// lisibilité ne peut que monter.
	for (let step = 1; step <= 40; step++) {
		const candidate = mix(background, away as Rgb, 1 - step / 40);
		if (separates(candidate)) return candidate;
	}
	return background;
}

export function mutedOn(foreground: Rgb, background: Rgb, minimum = 3): Rgb {
	let best = foreground;
	for (let step = 1; step <= 20; step++) {
		const candidate = mix(foreground, background, 1 - step / 20);
		if (contrast(candidate, background) < minimum) break;
		best = candidate;
	}
	return best;
}
